import {
    Button,
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    SimpleGrid,
} from '@chakra-ui/react';

import useCommonToast from '../../hooks/useCommonToast';

import { useIntl, FormattedMessage } from 'react-intl';

import { Formik, Form } from 'formik';

import { createForwardCurveDefinition } from '../../services/forwardCurve';

import InputFormikField from '../forms/InputFormikField';
import ComboboxFormikField from '../forms/ComboboxFormikField';
import CheckboxFormikField from '../forms/CheckboxFormikField';
import NumberInputFormikField from '../forms/NumberInputFormikField';

const AddForwardItemModal = ({ forwardItem, onAddSubItem, ...rest }) => {
    const toast = useCommonToast();
    const intl = useIntl();

    const formLabels = {
        selectPlaceholder: intl.formatMessage({ id: 'common_forms_select_option' }),
        name: intl.formatMessage({ id: 'add_new_forward_item_name' }),
        commodity: intl.formatMessage({ id: 'add_new_forward_item_commodity' }),
        peakPeriod: intl.formatMessage({ id: 'add_new_forward_item_peak_period' }),
        marketScaler: intl.formatMessage({ id: 'add_new_forward_item_market_scaler' }),
        currency: intl.formatMessage({ id: 'add_new_forward_item_currency' }),
        market: intl.formatMessage({ id: 'add_new_forward_item_market_name' }),
        useCurveDeveloper: intl.formatMessage({ id: 'add_new_forward_item_use_curve_developer' }),
    };

    const initialFormValues = {
        name: '',
        commodity: '',
        peakPeriod: '',
        currency: '',
        market: '',
        useCurveDeveloper: false,
    };

    const onFormSubmit = async ({ name, commodity, peakPeriod, marketScaler, currency, market, useCurveDeveloper }) => {
        const successMessage = intl.formatMessage(
            { id: 'common_sub_item_creation_success' },
            { type: 'forward curve' }
        );
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            const newForwardItem = await createForwardCurveDefinition({
                itemId: forwardItem.forwardPriceItem.id,
                description: name,
                commodityId: commodity,
                peakPeriodLabelId: peakPeriod,
                marketScaler: Number(marketScaler) || null,
                currencyId: currency || null,
                marketId: market || null,
                useCurveDeveloper,
            });
            onAddSubItem(newForwardItem);
            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    const validateForwardItemName = (value) => {
        const trimmed = value.trim();
        let errorMessage = '';

        if (trimmed.length === 0) {
            errorMessage = intl.formatMessage(
                { id: 'common_forms_validation_required' },
                { label: 'Forward Curve name' }
            );
        } else if (trimmed.length > 50) {
            errorMessage = intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label: 'Forward Curve name', lengthRule: '50 characters or less' }
            );
        }

        return errorMessage;
    };

    const validateRequiredField = (value, label) => {
        let errorMessage = '';

        if (value === '') {
            errorMessage = intl.formatMessage({ id: 'common_forms_validation_required' }, { label });
        }

        return errorMessage;
    };

    return (
        <Modal {...rest}>
            <ModalOverlay />

            <ModalContent pt={3}>
                <ModalHeader pr={12}>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="add_new_forward_item_heading" />
                    </Heading>
                </ModalHeader>
                <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                <Formik initialValues={initialFormValues} onSubmit={onFormSubmit}>
                    {({ isSubmitting, isValid, dirty }) => (
                        <Form>
                            <ModalBody borderBottom="1px" borderColor="gray.200">
                                <SimpleGrid columns={{ xl: 3 }} spacingX={6} spacingY={4} mb={4}>
                                    <InputFormikField
                                        id="name"
                                        name="name"
                                        type="text"
                                        label={formLabels.name}
                                        validate={(value) => validateForwardItemName(value)}
                                        isRequired
                                    />

                                    <ComboboxFormikField
                                        id="commodity"
                                        name="commodity"
                                        label={formLabels.commodity}
                                        validate={(value) => validateRequiredField(value, 'Commodity')}
                                        options={forwardItem.commodities}
                                        valueKey="id"
                                        labelKey="description"
                                        placeholderValue=""
                                        placeholderLabel={formLabels.selectPlaceholder}
                                        isRequired
                                    />

                                    <ComboboxFormikField
                                        id="peakPeriod"
                                        name="peakPeriod"
                                        label={formLabels.peakPeriod}
                                        validate={(value) => validateRequiredField(value, 'Peak Period')}
                                        options={forwardItem.peakPeriods}
                                        valueKey="id"
                                        labelKey="description"
                                        placeholderValue=""
                                        placeholderLabel={formLabels.selectPlaceholder}
                                        isRequired
                                    />

                                    <NumberInputFormikField
                                        id="marketScaler"
                                        name="marketScaler"
                                        type="text"
                                        label={formLabels.marketScaler}
                                    />

                                    <ComboboxFormikField
                                        id="currency"
                                        name="currency"
                                        label={formLabels.currency}
                                        options={forwardItem.currencies}
                                        valueKey="id"
                                        labelKey="description"
                                        placeholderValue=""
                                        placeholderLabel={formLabels.selectPlaceholder}
                                    />

                                    <ComboboxFormikField
                                        id="market"
                                        name="market"
                                        label={formLabels.market}
                                        options={forwardItem.marketNames}
                                        valueKey="id"
                                        labelKey="description"
                                        placeholderValue=""
                                        placeholderLabel={formLabels.selectPlaceholder}
                                    />

                                    <CheckboxFormikField id="useCurveDeveloper" name="useCurveDeveloper">
                                        {formLabels.useCurveDeveloper}
                                    </CheckboxFormikField>
                                </SimpleGrid>
                            </ModalBody>

                            <ModalFooter justifyContent="stretch" flexWrap="wrap">
                                <Box ml="auto">
                                    <Button variant="secondary" onClick={rest.onClose}>
                                        <Box as="span" textTransform="capitalize">
                                            <FormattedMessage id="common_cancel" />
                                        </Box>
                                    </Button>

                                    <Button
                                        type="submit"
                                        isDisabled={!isValid || !(dirty && isValid)}
                                        isLoading={isSubmitting}
                                        ml={3}
                                    >
                                        <FormattedMessage id="common_item_create_btn" />
                                    </Button>
                                </Box>
                            </ModalFooter>
                        </Form>
                    )}
                </Formik>
            </ModalContent>
        </Modal>
    );
};

export default AddForwardItemModal;
